/**
 * L'avatar : les initiales sur une teinte tirée du nom. La teinte est
 * DÉTERMINISTE, pour qu'une même personne garde la sienne d'un écran à l'autre —
 * une couleur qui change à chaque rendu se lit comme un bug.
 *
 * Pas de photo : aucun écran n'en a à montrer (les profils partagés n'en portent
 * pas). La variante reviendra avec la première donnée qui en a une.
 *
 * Les six teintes sont la rampe `persona` du proto, portée dans `tokens.css`. Ce
 * n'est pas la rampe `chip`, qui est catégorielle : elle habille des étiquettes, pas
 * des visages. Chaque fond a SON texte, déjà calculé pour être lisible dessus.
 */

const PERSONAS = [1, 2, 3, 4, 5, 6] as const;

const SIZE = {
	sm: "size-7 t-caption-strong",
	lg: "size-12 t-h4",
} as const;

function initials(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return (parts[0] ?? "").slice(0, 2).toUpperCase();
	return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
}

function personaFor(name: string): { backgroundColor: string; color: string } {
	let hash = 0;
	for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 9973;
	const index = PERSONAS[hash % PERSONAS.length] ?? 1;
	// ⚠️ Le nom du token est composé à l'exécution : le renommage global du 04/09
	// (`--mg-color-*` → `--mg-*`) ne l'a pas vu, et les avatars sans photo rendaient
	// des initiales sur fond transparent. `tests/brand.test.ts` lit désormais ce
	// fichier et vérifie que les tokens qu'il compose existent dans `tokens.css`.
	return {
		backgroundColor: `var(--mg-persona-${index}-bg)`,
		color: `var(--mg-persona-${index}-fg)`,
	};
}

export function Avatar({ name, size }: { name: string; size: keyof typeof SIZE }) {
	return (
		<span
			aria-hidden="true"
			style={personaFor(name)}
			className={`${SIZE[size]} inline-flex shrink-0 items-center justify-center rounded-full`}
		>
			{initials(name)}
		</span>
	);
}
